/* Transcribe a song: pick a bar, choose the chord you hear, check; then play along. */
import { useEffect, useRef, useState } from 'react';
import { useGo } from '../../app/navigation';
import type { PreparedSong } from '../../content/songs';
import { audio, type Player } from '../../core/audio';
import { addXp, award, saveLevel } from '../../core/store';
import { keyName, parseChord, pretty, QUAL_NAME } from '../../core/theory';
import { toast } from '../../core/toast';
import { scrollTop } from '../../core/util';
import { Btn } from '../../ui/Button';
import { Icon } from '../../ui/icons';
import { Rich } from '../../ui/Rich';
import { modStyle } from '../../ui/style';
import { ProgressBar } from '../../ui/widgets';
import { arrange } from './arrange';
import { LEVEL_NAME } from './SongList';
import { loadSongState, resetSongState, type SongState, saveSongState } from './state';

type Opt = { melody: boolean; bass: boolean; drums: boolean; chords: boolean; loop: boolean; tempo: number };

/** `onReset` re-mounts the page so every control returns to its default. */
export function SongPage({ song, onReset }: { song: PreparedSong; onReset: () => void }) {
  const go = useGo();
  const [st, setSt] = useState<SongState>(() => loadSongState(song.id));
  const okSet = new Set(st.ok);
  const [sel, setSel] = useState(() => {
    const ok = new Set(st.ok);
    const i = song.bars.findIndex((_, k) => !ok.has(k));
    return Math.max(i, 0);
  });
  const [opt, setOpt] = useState<Opt>({
    melody: !song.cover,
    bass: true,
    drums: true,
    chords: true,
    loop: false,
    tempo: 1,
  });
  const [loopSec, setLoopSec] = useState(-1);
  const [keyShown, setKeyShown] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(-1);
  const [status, setStatus] = useState<{ text: string; tone: string } | null>(null);
  const player = useRef<Player | null>(null);
  const chartEl = useRef<HTMLDivElement>(null);
  const paletteEl = useRef<HTMLDivElement>(null);
  const optRef = useRef(opt);
  optRef.current = opt;
  const loopRef = useRef(loopSec);
  loopRef.current = loopSec;

  useEffect(() => () => player.current?.stop(), []);

  const save = (next: SongState) => {
    setSt(next);
    saveSongState(song.id, next);
  };

  const startPlay = (fromBar?: number, toBar?: number) => {
    player.current?.stop();
    player.current = null;
    let from = 0;
    let to = song.bars.length;
    if (fromBar != null && toBar != null) {
      from = fromBar;
      to = toBar;
    } else if (loopRef.current >= 0) {
      from = song.bars.findIndex((b) => b.sec === loopRef.current);
      to = from + (song.sections[loopRef.current]?.bars.length ?? 0);
    }
    const o = optRef.current;
    const arr = arrange(song, { ...o, from, to });
    const p = audio.play(arr.ev, {
      length: arr.length,
      loop: fromBar == null && o.loop,
      onCue: (cue) => {
        if (cue.type === 'bar') setNow(cue.index);
      },
      onEnd: () => {
        if (player.current !== p) return;
        setNow(-1);
        setPlaying(false);
        player.current = null;
      },
    });
    player.current = p;
    setPlaying(true);
  };

  const togglePlay = async () => {
    await audio.resume();
    if (player.current) {
      player.current.stop();
      player.current = null;
      setPlaying(false);
      return;
    }
    startPlay();
  };

  const setOption = (key: keyof Omit<Opt, 'tempo'>) => {
    const next = { ...opt, [key]: !opt[key] };
    setOpt(next);
    optRef.current = next;
    if (key !== 'loop' && player.current) startPlay();
  };

  /** After a pick the selection moves on by itself: keep that bar in view above the sticky chord palette. */
  const showBar = (i: number) =>
    requestAnimationFrame(() => {
      const bar = chartEl.current?.querySelector<HTMLElement>(`[data-bar="${i}"]`);
      const pal = paletteEl.current;
      if (!bar || !pal) return;
      const hidden = bar.getBoundingClientRect().bottom + 8 - pal.getBoundingClientRect().top;
      if (hidden > 0) window.scrollBy({ top: hidden, behavior: 'smooth' });
    });

  const choose = (c: string | null) => {
    if (sel < 0 || okSet.has(sel)) return;
    const next: SongState = { ...st, ans: { ...st.ans }, wrong: st.wrong?.filter((x) => x !== sel) };
    if (c) {
      next.ans[sel] = c;
      audio.stopAll();
      player.current = null;
      setPlaying(false);
      audio.playChord(c, { dur: 1.3 });
    } else delete next.ans[sel];
    const after = song.bars.findIndex((_, i) => i > sel && !okSet.has(i) && !next.ans[i]);
    if (c && after >= 0) {
      setSel(after);
      showBar(after);
    }
    save(next);
  };

  const check = (base: SongState = st) => {
    const filled = Object.keys(base.ans).map(Number);
    if (!filled.length) {
      setStatus({ text: 'Önce birkaç ölçüye akor yaz.', tone: 't-info' });
      return;
    }
    const ok = new Set(base.ok);
    const wrong: number[] = [];
    for (const i of filled) {
      if (ok.has(i)) continue;
      if (base.ans[i] === song.bars[i]?.chord) ok.add(i);
      else wrong.push(i);
    }
    const next: SongState = { ...base, checks: base.checks + 1, wrong, ok: Array.from(ok) };
    const right = ok.size;
    if (right === song.bars.length && !next.solved) {
      next.solved = true;
      const stars = next.reveals === 0 && next.checks <= 2 ? 3 : next.reveals <= 2 && next.checks <= 5 ? 2 : 1;
      saveLevel(`song.${song.id}`, { stars, acc: 1, score: stars });
      addXp(40 + stars * 10, 'akor');
      award('sarki');
      toast(`Şarkıyı çözdün! +${40 + stars * 10} XP`, { tone: 'good', icon: 'award' });
    }
    save(next);
    if (!next.solved)
      setStatus({
        text: `${right}/${song.bars.length} ölçü doğru.${wrong.length ? ` ${wrong.length} ölçü yanlış: kırmızı ölçüleri tekrar dinle.` : ' Devam et.'}`,
        tone: wrong.length ? 't-bad' : 't-good',
      });
    setSel(song.bars.findIndex((_, i) => !ok.has(i)));
  };

  const reveal = () => {
    let i = sel;
    if (i < 0 || okSet.has(i)) i = song.bars.findIndex((_, k) => !okSet.has(k));
    if (i < 0) return;
    const chord = song.bars[i]?.chord as string;
    const next: SongState = {
      ...st,
      ans: { ...st.ans, [i]: chord },
      ok: [...st.ok, i],
      reveals: st.reveals + 1,
      wrong: st.wrong?.filter((x) => x !== i),
    };
    save(next);
    check(next);
  };

  let gi = 0;
  return (
    <div className="song-page" style={modStyle('akor')}>
      <button type="button" className="link-back" onClick={() => go('akor.sarkilar')}>
        <Icon name="left" />
        <span>Şarkılar</span>
      </button>
      <div className="song-head">
        <div className="sg-meta">
          <span className={`sg-tag lv${song.level}`}>{LEVEL_NAME[song.level]}</span>
          <span className="sg-tag">{song.style}</span>
          <span className="sg-tag">{song.beats}/4</span>
          <button type="button" className="sg-tag" onClick={() => setKeyShown(!keyShown)}>
            {keyShown ? `Tonalite: ${keyName(song.tonic, song.mode)}` : 'Tonaliteyi göster'}
          </button>
        </div>
        <h1>{song.title}</h1>
        <p>
          <Rich text={song.desc} />
        </p>
        {song.cover ? (
          <p className="mb-note">
            Orijinal kayıt: {song.cover.artist}
            {song.cover.capo ? ` · gitarda kapo ${song.cover.capo}. perdede çalınır` : ''}
            {song.cover.listen ? (
              <>
                {' · '}
                <a href={song.cover.listen} target="_blank" rel="noopener noreferrer">
                  Kaydı dinle
                </a>
              </>
            ) : null}
          </p>
        ) : null}
      </div>
      <div className="song-controls">
        <Btn kind="primary" icon={playing ? 'stop' : 'play'} onClick={togglePlay}>
          {playing ? 'Durdur' : 'Çal'}
        </Btn>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <select
            id="song-sec"
            aria-label="Bölüm"
            value={String(loopSec)}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value, 10);
              setLoopSec(v);
              loopRef.current = v;
              if (player.current) startPlay();
            }}
          >
            <option value="-1">Bütün şarkı</option>
            {song.sections.map((s, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: sections are positional and names may repeat
              <option key={i} value={String(i)}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <div className="tempo">
          Tempo
          <input
            id="song-tempo"
            aria-label="Tempo"
            type="range"
            min="0.6"
            max="1.1"
            step="0.05"
            value={opt.tempo}
            onChange={(e) => {
              const tempo = Number.parseFloat(e.target.value);
              setOpt({ ...opt, tempo });
              optRef.current = { ...opt, tempo };
            }}
          />
          <span>{Math.round(song.bpm * opt.tempo)} BPM</span>
        </div>
        <div className="toggles">
          {(
            [
              ['chords', 'Gitar'],
              ['bass', 'Bas'],
              ['drums', 'Davul'],
              ...(song.cover ? [] : [['melody', 'Melodi']]),
              ['loop', 'Döngü'],
            ] as [keyof Omit<Opt, 'tempo'>, string][]
          ).map(([key, label]) => (
            <button key={key} type="button" className={`tg${opt[key] ? ' on' : ''}`} onClick={() => setOption(key)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="ex-bar">
        <ProgressBar frac={okSet.size / song.bars.length} />
        <span className="ex-prog">
          {okSet.size}/{song.bars.length}
        </span>
      </div>
      <p className="ex-hint">
        Bir ölçü seç, sonra alttaki akorlardan birini seç. "Dinle" ile bölümü, "Seçili ölçüyü dinle" ile tek ölçüyü çal.
        İpucu: "Gitar"ı kapatıp yalnızca bası dinlemek kök notaları duymayı kolaylaştırır.
      </p>
      <div className="chart" ref={chartEl}>
        {song.sections.map((sec, si) => {
          const rows = Math.ceil(sec.bars.length / 4);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: sections are positional and names may repeat
            <div className="chart-sec" key={si}>
              <div className="cs-head">
                <span className="cs-name">{sec.name}</span>
                <Btn
                  kind="subtle"
                  icon="play"
                  onClick={async () => {
                    await audio.resume();
                    const from = song.bars.findIndex((b) => b.sec === si);
                    startPlay(from, from + sec.bars.length);
                  }}
                >
                  Dinle
                </Btn>
              </div>
              <div className="bars">
                {sec.bars.map((_, k) => {
                  const i = gi++;
                  const ok = okSet.has(i);
                  const a = st.ans[i];
                  const wrong = !!a && !ok && !!st.wrong?.includes(i);
                  const cls = [
                    'bar',
                    ok ? 'ok' : '',
                    wrong ? 'bad' : '',
                    sel === i && !ok ? 'sel' : '',
                    Math.floor(k / 4) === rows - 1 ? 'last-row' : '',
                    now === i ? 'now' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      key={i}
                      type="button"
                      className={cls}
                      aria-label={`${i + 1}. ölçü`}
                      data-bar={i}
                      onClick={() => {
                        if (ok) startPlay(i, i + 1);
                        else setSel(i);
                      }}
                    >
                      <span className="b-n">{i + 1}</span>
                      {a ? <span className="b-chord">{pretty(a)}</span> : <span className="b-q">?</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="palette sticky" ref={paletteEl}>
        {song.palette.map((c) => {
          const q = parseChord(c)?.q;
          return (
            <button key={c} type="button" className="pal-btn" data-chord={c} onClick={() => choose(c)}>
              <span className="pal-main">{pretty(c)}</span>
              <span className="pal-sub">{q != null ? QUAL_NAME[q] : ''}</span>
            </button>
          );
        })}
        <button type="button" className="pal-btn" onClick={() => choose(null)}>
          <span className="pal-main">–</span>
          <span className="pal-sub">sil</span>
        </button>
      </div>
      {st.solved ? (
        <div className="ex-feedback t-good">
          <strong>Şarkı çözüldü! </strong>Şimdi baştan sona birlikte çal.
        </div>
      ) : (
        <div className={`ex-feedback${status ? ` ${status.tone}` : ''}`}>{status?.text}</div>
      )}
      <div className="song-actions">
        <Btn kind="primary" icon="check" onClick={() => check()}>
          Kontrol et
        </Btn>
        <Btn
          kind="ghost"
          icon="play"
          onClick={async () => {
            await audio.resume();
            if (sel >= 0) startPlay(sel, sel + 1);
          }}
        >
          Seçili ölçüyü dinle
        </Btn>
        <Btn kind="subtle" icon="bulb" onClick={reveal}>
          Bir ölçüyü göster
        </Btn>
        <Btn kind={st.solved ? 'primary' : 'subtle'} icon="mic" onClick={() => go(`akor.sarki.${song.id}.birlikte`)}>
          {st.solved ? 'Birlikte çal' : 'Akorları görerek çal'}
        </Btn>
        <Btn kind="subtle" icon="jam" onClick={() => go(`jam.${song.id}`)}>
          Jam'de çal
        </Btn>
        <Btn
          kind="subtle"
          icon="replay"
          onClick={() => {
            resetSongState(song.id);
            audio.stopAll();
            scrollTop();
            onReset();
          }}
        >
          Sıfırla
        </Btn>
      </div>
    </div>
  );
}
